/**
 * stores/BaseStore.ts · 响应式 store 基类
 *
 * 简洁版 reactive (无依赖 · 替代 v200 module-level 变量散乱)
 *
 * 使用:
 *   class UserStore extends BaseStore<{ user: User | null }> { ... }
 *   const userStore = new UserStore({ user: null });
 *   const unsubscribe = userStore.subscribe(state => console.log(state.user));
 *   userStore.set({ user: someUser });
 */

export type Listener<T> = (state: T, prevState: T) => void;
export type Updater<T> = Partial<T> | ((s: T) => Partial<T>);

export class BaseStore<T extends object> {
  private state: T;
  private listeners = new Set<Listener<T>>();

  constructor(initial: T) {
    this.state = { ...initial };
  }

  /** 读 (返回浅拷贝, 防直接 mutate) */
  get(): T {
    return { ...this.state };
  }

  /** 单字段读 */
  getField<K extends keyof T>(key: K): T[K] {
    return this.state[key];
  }

  /** 写 (浅 merge · 触发 listeners) */
  set(updater: Updater<T>): void {
    const prev = { ...this.state };
    const patch = typeof updater === 'function' ? updater(this.state) : updater;
    this.state = { ...this.state, ...patch };
    this.notify(prev);
  }

  /** 重置 */
  reset(initial: T): void {
    const prev = { ...this.state };
    this.state = { ...initial };
    this.notify(prev);
  }

  /** 订阅 (返回取消函数) */
  subscribe(fn: Listener<T>): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** 订阅单字段变化 */
  subscribeField<K extends keyof T>(key: K, fn: (value: T[K], prevValue: T[K]) => void): () => void {
    return this.subscribe((state, prev) => {
      if (state[key] !== prev[key]) fn(state[key], prev[key]);
    });
  }

  private notify(prev: T): void {
    for (const fn of this.listeners) {
      try { fn(this.state, prev); } catch (e) { console.error('[store listener error]', e); }
    }
  }

  /** debug · 列出当前 listener 数 */
  get listenerCount(): number {
    return this.listeners.size;
  }
}
